import { Entity } from '@backstage/catalog-model';
import {
  ApiBlueprint,
  createFrontendModule,
  IconComponent,
} from '@backstage/frontend-plugin-api';
import {
  catalogApiRef,
  defaultEntityPresentation,
  entityPresentationApiRef,
} from '@backstage/plugin-catalog-react';
import { DefaultEntityPresentationApi } from '@backstage/plugin-catalog';
import { GcpIconName } from '@internal/plugin-gcp-network-diagram';
import { annotatedIcon, gcpIconComponents } from './icons/catalogIcons';

/**
 * GCP product icon per Cloud Asset Inventory type, as written by the GCP asset
 * ingestor into the `gcp.com/asset-type` annotation. The asset type is more
 * precise than `spec.type` (generic: "database", "cache") or `gcp.com/service`
 * ("gcp-network" covers VPCs, subnets and routers alike).
 */
const iconByAssetType: Record<string, GcpIconName> = {
  'sqladmin.googleapis.com/Instance': 'cloudSql',
  'redis.googleapis.com/Instance': 'memorystore',
  'container.googleapis.com/Cluster': 'googleKubernetesEngine',
  'storage.googleapis.com/Bucket': 'cloudStorage',
  'compute.googleapis.com/UrlMap': 'cloudLoadBalancing',
  'compute.googleapis.com/BackendService': 'cloudLoadBalancing',
  'compute.googleapis.com/BackendBucket': 'cloudLoadBalancing',
  'compute.googleapis.com/ForwardingRule': 'cloudLoadBalancing',
  'compute.googleapis.com/TargetHttpProxy': 'cloudLoadBalancing',
  'compute.googleapis.com/TargetHttpsProxy': 'cloudLoadBalancing',
  'compute.googleapis.com/Network': 'virtualPrivateCloud',
  'compute.googleapis.com/Subnetwork': 'cloudNetwork',
  'compute.googleapis.com/Router': 'cloudRouter',
  'compute.googleapis.com/Firewall': 'cloudFirewallRules',
  'dns.googleapis.com/ManagedZone': 'cloudDns',
  'dns.googleapis.com/ResourceRecordSet': 'cloudDns',
};

/** For asset types added to the ingestor before they get an entry above. */
const iconByService: Record<string, GcpIconName> = {
  cloudsql: 'cloudSql',
  memorystore: 'memorystore',
  gke: 'googleKubernetesEngine',
  gcs: 'cloudStorage',
  'gcp-lb': 'cloudLoadBalancing',
  'gcp-firewall': 'cloudFirewallRules',
  'cloud-dns-zone': 'cloudDns',
  'cloud-dns-record': 'cloudDns',
};

const gcpIconFor = (entity: Entity): IconComponent | undefined => {
  const annotations = entity.metadata.annotations ?? {};
  const name =
    iconByAssetType[annotations['gcp.com/asset-type']] ??
    iconByService[annotations['gcp.com/service']];
  return name && gcpIconComponents[name];
};

/**
 * An icon the entity chose itself (`backstage.ds25.io/icon`) wins; then the
 * GCP product icon for ingested resources; `undefined` keeps the kind icon.
 */
export const iconFor = (
  entity: Entity | undefined,
): IconComponent | undefined =>
  entity && (annotatedIcon(entity.metadata.annotations) ?? gcpIconFor(entity));

/**
 * Replaces the catalog plugin's own `api:catalog/entity-presentation`
 * extension (same plugin and name), which is how the New Frontend System
 * expects an API to be swapped out. Everything but the icon is Backstage's
 * default presentation; entities without an icon of their own keep their
 * kind icon.
 */
export const catalogEntityPresentationModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    ApiBlueprint.make({
      name: 'entity-presentation',
      params: defineParams =>
        defineParams({
          api: entityPresentationApiRef,
          deps: { catalogApi: catalogApiRef },
          factory: ({ catalogApi }) =>
            DefaultEntityPresentationApi.create({
              catalogApi,
              renderer: {
                async: true,
                render: ({ entityRef, entity, context }) => ({
                  snapshot: {
                    ...defaultEntityPresentation(entity ?? entityRef, context),
                    Icon: iconFor(entity),
                  },
                }),
              },
            }),
        }),
    }),
  ],
});
